import { useState } from "react"
import { PlayIcon, CheckIcon } from "@heroicons/react/20/solid"
import useTranslations from "hooks/useTranslations"
import { VideoCoverImage } from "./images"

/**
 * Sits directly under the hero, per Essi's Tab 3 spec.
 *
 * The player is a facade: a cover frame and a play button, with the YouTube
 * iframe mounted only once someone clicks. A live embed would pull YouTube's
 * player JS on every page load whether or not anyone watches, which on a page
 * taking paid traffic is a cost paid for nothing. It also means no third-party
 * request at all until the visitor asks for one.
 *
 * `youtube-nocookie.com` for the same reason every other embed on this site
 * uses it: the standard domain sets tracking cookies before playback, which
 * the consent banner has not asked about yet.
 */
export default function UkVideoIntro() {
  const { ukSchoolsVideoTitle, ukSchoolsVideoPoints, ukSchoolsVideoId } =
    useTranslations()
  const [playing, setPlaying] = useState(false)

  return (
    <div className="bg-zinc-800 py-16 lg:py-24">
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 items-center gap-y-10 lg:grid-cols-2 lg:gap-x-16">
          <div className="space-y-8">
            <h2 className="m-0 whitespace-pre-line font-display text-4xl font-extrabold leading-tight tracking-tight text-zinc-100 lg:text-5xl">
              {ukSchoolsVideoTitle}
            </h2>
            <ul className="m-0 list-none space-y-4 p-0">
              {(ukSchoolsVideoPoints || []).map((point) => (
                <li key={point} className="flex items-start gap-3">
                  <CheckIcon
                    aria-hidden="true"
                    className="mt-0.5 h-6 w-6 shrink-0 text-kkm-lavender"
                  />
                  <span className="text-base text-zinc-100 lg:text-lg">
                    {point}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* A 1px pure-white ring at low opacity keeps the cover's edge
              defined against the dark band. Pure white rather than a tinted
              neutral, which would pick up the surface colour and read as dirt
              on the image edge. The ring sits on the container so it follows
              the corner radius instead of being clipped square. */}
          <div className="overflow-hidden rounded-2xl bg-zinc-900 ring-1 ring-white/10">
            <div className="relative h-0 pb-fluid-video">
              {playing && ukSchoolsVideoId ? (
                <iframe
                  className="absolute inset-0 h-full w-full"
                  src={`https://www.youtube-nocookie.com/embed/${ukSchoolsVideoId}?autoplay=1&rel=0`}
                  title={ukSchoolsVideoTitle}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              ) : (
                <button
                  type="button"
                  onClick={() => setPlaying(true)}
                  disabled={!ukSchoolsVideoId}
                  aria-label={`Play video: ${ukSchoolsVideoTitle}`}
                  className="group absolute inset-0 h-full w-full cursor-pointer border-0 bg-transparent p-0 focus:outline-none focus-visible:ring-4 focus-visible:ring-kkm-green"
                >
                  <VideoCoverImage className="h-full w-full" />
                  <span
                    aria-hidden="true"
                    className="absolute inset-0 flex items-center justify-center bg-zinc-900/20 transition-colors group-hover:bg-zinc-900/40"
                  >
                    {/* Scale the play control, not the whole cover: pressing a
                        full-bleed image and watching it shrink reads as the
                        page moving rather than a button responding. */}
                    <span className="flex h-20 w-20 items-center justify-center rounded-full bg-white/90 shadow-lg transition-transform duration-150 ease-out group-hover:scale-105 group-active:scale-[0.96]">
                      {/* ml-1 optically centres the triangle: a play glyph's
                          visual mass sits left of its bounding box. */}
                      <PlayIcon className="ml-1 h-10 w-10 text-zinc-900" />
                    </span>
                  </span>
                </button>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
